import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Eye, EyeOff, Pin, PinOff, ChevronDown, ChevronRight } from 'lucide-react';

interface DocumentEditorProps {
  initialTitle?: string;
  initialBody?: string;
  initialPinned?: boolean;
  initialFolder?: string | null;
  initialSummary?: string | null;
  onSave: (data: { title: string; body: string; is_pinned?: boolean; folder?: string | null; summary?: string | null }) => void;
  onCancel: () => void;
  isSaving?: boolean;
  maxBodyChars?: number;
  maxTitleChars?: number;
  maxSummaryChars?: number;
  showPinToggle?: boolean;
  hideTitle?: boolean;
}

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  initialTitle = '',
  initialBody = '',
  initialPinned = false,
  initialFolder = null,
  initialSummary = null,
  onSave,
  onCancel,
  isSaving = false,
  maxBodyChars = 12000,
  maxTitleChars = 200,
  maxSummaryChars = 400,
  showPinToggle = false,
  hideTitle = false,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [isPinned, setIsPinned] = useState(initialPinned);
  const [folder, setFolder] = useState(initialFolder || '');
  const [summary, setSummary] = useState(initialSummary || '');
  const [showPreview, setShowPreview] = useState(false);
  const [showExtras, setShowExtras] = useState(
    !!(initialFolder || initialSummary)
  );

  useEffect(() => {
    setTitle(initialTitle);
    setBody(initialBody);
    setIsPinned(initialPinned);
    setFolder(initialFolder || '');
    setSummary(initialSummary || '');
    setShowExtras(!!(initialFolder || initialSummary));
  }, [initialTitle, initialBody, initialPinned, initialFolder, initialSummary]);

  const canSave = title.trim().length > 0 && body.trim().length > 0 && body.length <= maxBodyChars && title.length <= maxTitleChars && summary.length <= maxSummaryChars;

  const extrasCount = (folder.trim() ? 1 : 0) + (summary.trim() ? 1 : 0);

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      title: title.trim(),
      body,
      is_pinned: isPinned,
      folder: folder.trim() || '',
      summary: summary.trim() || '',
    });
  };

  return (
    <div className="space-y-3">
      {!hideTitle && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              maxLength={maxTitleChars}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {showPinToggle && (
              <button
                type="button"
                onClick={() => setIsPinned(!isPinned)}
                className={`p-1.5 rounded-md transition-colors ${
                  isPinned
                    ? 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                }`}
                title={isPinned ? 'Unpin document' : 'Pin document'}
              >
                {isPinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
              </button>
            )}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 text-right">
            {title.length}/{maxTitleChars}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <span className={`text-xs ${body.length > maxBodyChars ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'}`}>
            {body.length}/{maxBodyChars}
          </span>
        </div>

        {showPreview ? (
          <div className="min-h-[120px] max-h-[400px] overflow-y-auto px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || '*No content*'}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Document body (supports Markdown)"
            rows={8}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y min-h-[120px]"
          />
        )}
      </div>

      {/* Progressive disclosure for folder + summary */}
      <div>
        <button
          type="button"
          onClick={() => setShowExtras(!showExtras)}
          className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          {showExtras ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          More fields
          {!showExtras && extrasCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
              {extrasCount}
            </span>
          )}
        </button>

        {showExtras && (
          <div className="mt-2 space-y-2">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Folder</label>
              <input
                type="text"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                placeholder="e.g. research/sleep (max 3 levels)"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <label className="text-xs text-gray-500 dark:text-gray-400">Summary</label>
                <span className={`text-xs ${summary.length > maxSummaryChars ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'}`}>
                  {summary.length}/{maxSummaryChars}
                </span>
              </div>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Short description used in search results and agent context"
                rows={2}
                maxLength={maxSummaryChars}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave || isSaving}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};
